import { useState, useEffect } from "react";
import { Container, Card, Button, Alert, Spinner, Form } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

function Checkout({ date, clientSecret, couponCode, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [isReady, setIsReady] = useState(false);

  const submit = async () => {
    if (!stripe || !elements) {
      setError("Stripe is not loaded. Please refresh the page.");
      return;
    }

    // Check if PaymentElement is mounted
    const paymentElement = elements.getElement("payment");
    if (!paymentElement) {
      setError("Payment form is not ready. Please wait a moment and try again.");
      return;
    }
    
    setProcessing(true);
    setError("");

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });

      if (confirmError) {
        setError(confirmError.message || "Payment failed. Please try again.");
        setProcessing(false);
        return;
      }

      // Fetch the payment intent from Stripe using clientSecret
      const intentResult = await stripe.retrievePaymentIntent(clientSecret);
      
      if (!intentResult.paymentIntent) {
        setError("Unable to retrieve payment information. Please try again.");
        setProcessing(false);
        return;
      }

      if (intentResult.paymentIntent.status === "succeeded") {
        const res = await fetch("/api/days/purchase/confirm", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            date,
            paymentIntentId: intentResult.paymentIntent.id,
            couponCode: couponCode || undefined,
          }),
        });

        const data = await res.json();

        if (res.ok) {
          onSuccess();
        } else {
          setError(data.message || "Failed to confirm purchase. Please contact support.");
          setProcessing(false);
          if (onError) onError(data.message || "Failed to confirm purchase");
        }
      } else {
        setError(`Payment status: ${intentResult.paymentIntent.status}. Please try again.`);
        setProcessing(false);
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred. Please try again.");
      setProcessing(false);
      if (onError) onError(err.message || "An unexpected error occurred");
    }
  };

  return (
    <>
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <PaymentElement 
        onReady={() => {
          setIsReady(true);
        }}
        options={{
          layout: "tabs"
        }}
      />
      {!isReady && (
        <Alert variant="info" className="mt-3">
          <Spinner size="sm" className="me-2" />
          Loading payment form...
        </Alert>
      )}
      <Button
        className="w-100 mt-3"
        size="lg"
        disabled={processing || !isReady}
        onClick={submit}
      >
        {processing ? (
          <>
            <Spinner size="sm" /> Processing…
          </>
        ) : (
          "Pay & Purchase"
        )}
      </Button>
    </>
  );
}

export default function Buy() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stripeError, setStripeError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  
  // Purchase new day state
  const [date, setDate] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [isFree, setIsFree] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState("");
  const [purchaseSuccess, setPurchaseSuccess] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [originalAmount, setOriginalAmount] = useState(1500); // Default, will be updated from API

  const token = localStorage.getItem("token");

  // Fetch pricing and Stripe publishable key from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Fetch pricing
        const pricingRes = await fetch("/api/pricing");
        if (pricingRes.ok) {
          const pricingData = await pricingRes.json();
          setOriginalAmount(pricingData.dayPurchaseCost || 1500);
        }

        // Fetch Stripe publishable key
        const res = await fetch("/api/stripe-publishable-key");
        const data = await res.json();
        
        if (res.ok && data.publishableKey) {
          // Load Stripe with the key from backend
          const promise = loadStripe(data.publishableKey);
          promise.catch((error) => {
            console.error("Failed to load Stripe.js:", error);
            setStripeError("Failed to load Stripe.js. Please check your internet connection and try refreshing the page.");
          });
          setStripePromise(promise);
        } else {
          setStripeError("Failed to retrieve Stripe configuration. Please refresh the page.");
        }
      } catch (error) {
        console.error("Error fetching configuration:", error);
        setStripeError("Failed to initialize. Please refresh the page.");
      } finally {
        setStripeLoading(false);
      }
    };

    fetchConfig();
  }, []);


  // Pre-fill date from URL parameter
  useEffect(() => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      setDate(dateParam);
    }
  }, [searchParams]);

  const validateCoupon = async (code, purchaseType, customAmount = null) => {
    const baseAmount = customAmount || (purchaseType === "new" ? originalAmount : 0);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch("/api/coupons/validate", {
        method: "POST",
        headers,
        body: JSON.stringify({
          code,
          amount: baseAmount,
          purchaseType,
        }),
      });

      const data = await res.json();
      return { success: res.ok, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const startPurchase = async () => {
    if (!date) return;

    // Validate date range (1900-2100)
    const dateObj = new Date(date + "T00:00:00");
    const year = dateObj.getFullYear();
    if (year < 1900 || year > 2100) {
      setPurchaseError("Date must be between 1900 and 2100");
      return;
    }

    setPurchaseLoading(true);
    setPurchaseError("");
    setPurchaseSuccess("");
    setCouponDiscount(null);
    
    try {
      const res = await fetch("/api/days/purchase/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          date,
          couponCode: couponCode || undefined,
        }),
      });

      const data = await res.json();
      
      if (res.ok) {
        // Check if this is a free purchase (amount is 0)
        if (data.isFree) {
          setIsFree(true);
          setClientSecret(null);
          // For free purchases, directly confirm without Stripe
          await confirmFreePurchase(data);
        } else {
          setIsFree(false);
          setClientSecret(data.clientSecret);
          if (data.coupon) {
            setCouponDiscount({
              discountAmount: data.discountAmount,
              originalAmount: data.originalAmount,
              finalAmount: data.finalAmount,
              coupon: data.coupon,
            });
          }
        }
      } else {
        setPurchaseError(data.message || "Failed to start payment");
        setClientSecret(null);
        setIsFree(false);
      }
    } catch (err) {
      setPurchaseError(err.message || "Failed to start payment");
      setClientSecret(null);
    }
    setPurchaseLoading(false);
  };

  const confirmFreePurchase = async (purchaseData) => {
    try {
      setPurchaseLoading(true);
      setPurchaseError("");
      
      const res = await fetch("/api/days/purchase/confirm", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          date,
          paymentIntentId: null,
          isFree: true,
          couponCode: couponCode || undefined,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setPurchaseSuccess(`Day ${date} successfully purchased for free! 🎉`);
        setDate("");
        setCouponCode("");
        setCouponDiscount(null);
        setIsFree(false);
        // Redirect to registry after 2 seconds
        setTimeout(() => {
          navigate("/registry");
        }, 2000);
      } else {
        setPurchaseError(data.message || "Failed to confirm free purchase");
      }
    } catch (err) {
      setPurchaseError(err.message || "An error occurred. Please try again.");
    } finally {
      setPurchaseLoading(false);
    }
  };

  return (
    <>
    <Container className="mt-5 px-3 px-md-4" style={{ maxWidth: 700 }}>
      {stripeError && (
        <Alert variant="danger" className="brutal-alert mb-4">
          <strong>PAYMENT SYSTEM ERROR:</strong> {stripeError.toUpperCase()}
          <br />
          <div className="brutal-text" style={{ fontSize: "12px", marginTop: "8px" }}>
            IF THIS PROBLEM PERSISTS, PLEASE CHECK YOUR INTERNET CONNECTION OR CONTACT SUPPORT.
          </div>
        </Alert>
      )}
      
      {/* Purchase a new day section */}
      <Card className="brutal-card mb-4">
        <Card.Body className="p-4">
          <Card.Title className="brutal-heading mb-4" style={{ fontSize: "32px" }}>PURCHASE A NEW DAY</Card.Title>
          <Alert variant="info" className="brutal-alert mt-3 mb-4">
            SELECT ANY DATE (PAST OR FUTURE) AND COMPLETE PAYMENT TO REGISTER IT UNDER YOUR NAME. 
            DATES THAT ARE ALREADY PURCHASED CANNOT BE SELECTED.
          </Alert>

          {purchaseError && <Alert variant="danger" className="brutal-alert mb-3">{purchaseError.toUpperCase()}</Alert>}
          {purchaseSuccess && <Alert variant="success" className="brutal-alert mb-3">{purchaseSuccess.toUpperCase()}</Alert>}

          <Form>
            <Form.Group className="mt-3">
              <Form.Label className="brutal-subheading mb-2">SELECT DATE</Form.Label>
              <Form.Control
                type="date"
                value={date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  setDate(selectedDate);
                  // Clear error when user changes date
                  if (purchaseError && purchaseError.includes("1900") && purchaseError.includes("2100")) {
                    setPurchaseError("");
                  }
                }}
                className="brutal-form-control"
                disabled={purchaseLoading}
                min="1900-01-01"
                max="2100-12-31"
              />
              <Form.Text className="text-muted" style={{ fontSize: "12px", fontFamily: "'Courier New', monospace" }}>
                DATE MUST BE BETWEEN 1900 AND 2100
              </Form.Text>
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label className="brutal-subheading mb-2">COUPON CODE (OPTIONAL)</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="ENTER COUPON CODE"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="brutal-form-control"
                  disabled={purchaseLoading || !!clientSecret}
                />
                {couponCode && !clientSecret && (
                  <Button
                    className="brutal-btn brutal-btn-secondary"
                    onClick={async () => {
                      const result = await validateCoupon(couponCode, "new");
                      if (result.success && result.data.valid) {
                        setCouponDiscount(result.data);
                      } else {
                        setPurchaseError(result.data?.message || "Invalid coupon code");
                        setCouponDiscount(null);
                      }
                    }}
                    disabled={purchaseLoading}
                  >
                    VALIDATE
                  </Button>
                )}
              </div>
              {couponDiscount && (
                <Alert variant="success" className="brutal-alert mt-2 mb-0">
                  <strong>COUPON APPLIED!</strong> {couponDiscount.coupon.description || couponDiscount.coupon.code}
                  <br />
                  <div className="brutal-text" style={{ fontSize: "12px", marginTop: "8px" }}>
                    DISCOUNT: ${(couponDiscount.discountAmount / 100).toFixed(2)} | 
                    FINAL PRICE: ${(couponDiscount.finalAmount / 100).toFixed(2)}
                  </div>
                </Alert>
              )}
            </Form.Group>

            {couponDiscount && (
              <div className="mt-2 mb-3 p-3" style={{ border: "4px solid #000000", background: "#FFFFFF" }}>
                <div className="d-flex justify-content-between brutal-text mb-2">
                  <span>ORIGINAL PRICE:</span>
                  <span>${(couponDiscount.originalAmount / 100).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between brutal-text mb-2" style={{ color: "#00FF00" }}>
                  <span>DISCOUNT:</span>
                  <span>-${(couponDiscount.discountAmount / 100).toFixed(2)}</span>
                </div>
                <div className="brutal-divider"></div>
                <div className="d-flex justify-content-between brutal-heading" style={{ fontSize: "20px" }}>
                  <span>TOTAL:</span>
                  <span>${(couponDiscount.finalAmount / 100).toFixed(2)}</span>
                </div>
              </div>
            )}

            {!couponDiscount && (
              <div className="mt-2 mb-3 p-3 text-center" style={{ border: "4px solid #000000", background: "#FFFFFF" }}>
                <strong className="brutal-heading" style={{ fontSize: "20px" }}>PRICE: ${(originalAmount / 100).toFixed(2)}</strong>
              </div>
            )}

            <Button
              className="brutal-btn brutal-btn-primary w-100 mt-3"
              size="lg"
              disabled={!date || purchaseLoading}
              onClick={startPurchase}
            >
              {purchaseLoading ? (
                <>
                  <Spinner size="sm" className="me-2" /> STARTING PAYMENT…
                </>
              ) : (
                "CONTINUE TO PAYMENT"
              )}
            </Button>
          </Form>

          {clientSecret && !stripeError && !isFree && stripePromise && !stripeLoading && (
            <div className="mt-4">
              <div className="brutal-divider"></div>
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <Checkout
                  date={date}
                  clientSecret={clientSecret}
                  couponCode={couponCode}
                  onSuccess={async () => {
                    setPurchaseError("");
                    setPurchaseSuccess(`Day ${date} successfully purchased 🎉`);
                    setClientSecret(null);
                    setDate("");
                    setCouponCode("");
                    setCouponDiscount(null);
                    setIsFree(false);
                    // Redirect to registry after 2 seconds
                    setTimeout(() => {
                      navigate("/registry");
                    }, 2000);
                  }}
                  onError={(errorMsg) => {
                    setPurchaseError(errorMsg || "Payment failed. Please try again.");
                  }}
                />
              </Elements>
            </div>
          )}
          {stripeLoading && (
            <Alert variant="info" className="brutal-alert mt-4">
              <Spinner size="sm" className="me-2" />
              LOADING PAYMENT SYSTEM...
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Buy from marketplace section - Coming Soon */}
      <Card className="brutal-card mb-4">
        <Card.Body className="p-4">
          <Card.Title className="brutal-heading mb-4" style={{ fontSize: "32px" }}>BUY FROM MARKETPLACE</Card.Title>
          
          <div className="text-center py-4">
            <div className="brutal-coming-soon-box mb-4">
              <h3 className="brutal-heading mb-3" style={{ fontSize: "32px", color: "#FFFF00" }}>
                COMING SOON
              </h3>
              <div className="brutal-divider mb-3"></div>
              <p className="brutal-text mb-0" style={{ fontSize: "14px" }}>
                THE MARKETPLACE FEATURE IS CURRENTLY UNDER DEVELOPMENT.
                <br />
                YOU WILL SOON BE ABLE TO BUY AND SELL REGISTERED DAYS.
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Container>
    <style>{`
      .brutal-coming-soon-box {
        background: #000000;
        border: 5px solid #FFFF00;
        padding: 30px;
        box-shadow: 10px 10px 0px #FFFF00;
        max-width: 500px;
        margin: 0 auto;
      }
      .brutal-coming-soon-box .brutal-divider {
        background: #FFFF00;
        height: 4px;
        width: 150px;
        margin: 15px auto;
      }
    `}</style>
    </>
  );
}
